import { View, Text, styled, useTheme } from 'tamagui'
import { SmilePlus, X } from '@tamagui/lucide-icons'
import { useState } from 'react'

const Popover = styled(View, {
  width: 260,
  backgroundColor: '$surfaceRaised',
  borderRadius: '$borderRadiusLg',
  borderWidth: 1,
  borderColor: '$borderDefault',
  padding: 12,
  gap: 10,
})

const SectionLabel = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 11,
  fontWeight: '600',
  color: '$color8',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
})

const InputRow = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
})

const EmojiButton = styled(View, {
  width: 32,
  height: 32,
  borderRadius: '$borderRadiusMd',
  borderWidth: 1,
  borderColor: '$borderDefault',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease' },
})

const ExpiryRow = styled(View, {
  gap: 4,
})

const ExpiryOption = styled(View, {
  height: 28,
  borderRadius: '$borderRadiusMd',
  paddingHorizontal: 10,
  justifyContent: 'center',
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease' },
  variants: {
    active: {
      true: {
        backgroundColor: '$accentLighter',
      },
    },
  } as const,
})

const ExpiryText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 12,
  color: '$surfaceForeground',
})

const ButtonRow = styled(View, {
  flexDirection: 'row',
  gap: 8,
})

const ActionButton = styled(View, {
  flex: 1,
  height: 32,
  borderRadius: '$borderRadiusMd',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease, opacity 150ms ease' },
  variants: {
    variant: {
      save: {
        backgroundColor: '$accent',
        hoverStyle: { opacity: 0.9 },
      },
      clear: {
        borderWidth: 1,
        borderColor: '$borderDefault',
        hoverStyle: { backgroundColor: '$sidebarHover' },
      },
    },
  } as const,
})

const ButtonText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 12,
  fontWeight: '600',
})

const STATUS_PRESETS = [
  { emoji: '🎮', text: 'Gaming' },
  { emoji: '📚', text: 'Studying' },
  { emoji: '⛔', text: 'Do Not Disturb' },
  { emoji: '📅', text: 'In a Meeting' },
  { emoji: '💻', text: 'Coding' },
  { emoji: '🎧', text: 'Listening to Music' },
]

const PresetButton = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
  paddingHorizontal: 8,
  paddingVertical: 5,
  borderRadius: '$borderRadiusMd',
  cursor: 'pointer',
  hoverStyle: { backgroundColor: '$sidebarHover' },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 100ms ease' },
})

type ExpiryOption = 'never' | '4h' | 'today' | 'week'

const expiryLabels: { value: ExpiryOption; label: string }[] = [
  { value: 'never', label: "Don't clear" },
  { value: '4h', label: '4 hours' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This week' },
]

interface StatusPickerProps {
  onClose: () => void
}

export function StatusPicker({ onClose }: StatusPickerProps) {
  const theme = useTheme()
  const [statusText, setStatusText] = useState('')
  const [emoji, setEmoji] = useState('')
  const [expiry, setExpiry] = useState<ExpiryOption>('never')

  const handleSave = () => {
    // Mock save — in a real app this would update the user's status
    onClose()
  }

  const handleClear = () => {
    setStatusText('')
    setEmoji('')
    setExpiry('never')
    onClose()
  }

  return (
    <Popover
      // @ts-expect-error -- web box-shadow
      style={{ boxShadow: theme.shadowCard?.val || 'none' }}
    >
      <SectionLabel>Set Custom Status</SectionLabel>

      <InputRow>
        <EmojiButton onPress={() => setEmoji(emoji ? '' : '\u{1F680}')}>
          {emoji ? (
            <Text fontSize={16}>{emoji}</Text>
          ) : (
            <SmilePlus size={16} color="$sidebarMuted" />
          )}
        </EmojiButton>
        <View flex={1}>
          <input
            value={statusText}
            onChange={(e) => setStatusText(e.target.value)}
            placeholder="What's happening?"
            style={{
              background: 'transparent',
              border: `1px solid ${theme.borderDefault?.val || '#2A3142'}`,
              borderRadius: 6,
              padding: '6px 10px',
              color: theme.surfaceForeground?.val || '#E2E8F0',
              fontFamily: 'inherit',
              fontSize: 13,
              outline: 'none',
              width: '100%',
              boxSizing: 'border-box' as const,
            }}
          />
        </View>
      </InputRow>

      <View gap={2}>
        <SectionLabel>Quick Set</SectionLabel>
        {STATUS_PRESETS.map((preset) => (
          <PresetButton key={preset.text} onPress={() => { setEmoji(preset.emoji); setStatusText(preset.text) }}>
            <Text fontSize={14}>{preset.emoji}</Text>
            <Text fontFamily="$fontBody" fontSize={12} color="$surfaceForeground">{preset.text}</Text>
          </PresetButton>
        ))}
      </View>

      <ExpiryRow>
        <SectionLabel>Clear After</SectionLabel>
        {expiryLabels.map(({ value, label }) => (
          <ExpiryOption
            key={value}
            active={expiry === value}
            onPress={() => setExpiry(value)}
          >
            <ExpiryText
              color={expiry === value ? '$accent' : '$surfaceForeground'}
            >
              {expiry === value ? '\u25CF ' : ''}{label}
            </ExpiryText>
          </ExpiryOption>
        ))}
      </ExpiryRow>

      <ButtonRow>
        <ActionButton variant="clear" onPress={handleClear}>
          <ButtonText color="$sidebarMuted">Clear</ButtonText>
        </ActionButton>
        <ActionButton variant="save" onPress={handleSave}>
          <ButtonText color="$surface">Save</ButtonText>
        </ActionButton>
      </ButtonRow>
    </Popover>
  )
}
